import React, { useState } from "react";
import { FormProvider } from "../../provider/Form/FormProvider";
import AuthActionForm from "./AuthActionForm";
import * as loginActions from "../../store/login/actions";
import { login, register } from "../../store/login/actions";
import * as messagesActions from "../../store/messages/actions";
import { NavigateProps, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../services/reactQuery/reactQueryService";
import { apiGet, getToken } from "../../services/apiClient";
import { AuthActionType } from "@/src/types/AuthActionType";
import { FormDataTypes } from "@/src/types/FormDataTypes";
import { useAuthDialog } from "@/src/provider/AuthDialog/AuthDialogProvider";

type AuthActionProps = {
  actionType?: AuthActionType;
  showPostfix?: boolean;
  initialEmail?: string;
  redirectTo?: NavigateProps["to"] | null;
  onActionTypeChange: (actionType: AuthActionType) => void;
};

export default function AuthAction({ onActionTypeChange, actionType = "login", showPostfix = true, initialEmail = "", redirectTo }: AuthActionProps) {
  const authDialog = useAuthDialog();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const initialValues = {
    email: initialEmail,
    password: "",
    gender: "",
    familyName: "",
    givenName: "",
  };

  const handleLogin = async (values: FormDataTypes["values"]) => {
    setIsLoading(true);
    try {
      await getToken({ email: values.email.toLowerCase(), password: values.password });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("/customers"),
      });
      const from = redirectTo || { pathname: "/servicewelt", search: "" };
      navigate(from);
      /* Closes dialog if opened */
      authDialog?.setView(null);
    } catch (e) {
      messagesActions.addMessage({
        type: "warning",
        text: "Für diese Kombination aus E-Mail-Adresse und Passwort konnten keine Daten gefunden werden. Bitte überprüfen Sie Ihre eingegebenen Daten.",
      });
      setIsLoading(false);
    }
  };

  const handleRegister = async (values: FormDataTypes["values"]) => {
    setIsLoading(true);
    const emailCheck = await loginActions.checkEmail(values.email.toLowerCase());
    if (emailCheck.hasError) {
      setIsLoading(false);
      messagesActions.addMessage({
        type: "info",
        text: "Es existiert bereits ein Konto mit dieser E-Mail-Adresse. Sie können sich mit Ihrem Passwort einloggen.",
      });
      onActionTypeChange("login");
      return;
    }

    const data = {
      email: values.email.toLowerCase(),
      person: {
        givenName: values.givenName,
        familyName: values.familyName,
      },
      prelead: {
        initialProduct: "generic",
        landingPage: "RegisterBox",
      },
    };

    try {
      await register(data);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.collection("/customers"),
      });
      const from = redirectTo || { pathname: "/servicewelt", search: "" };
      navigate(from);
      /* Closes dialog if opened */
      authDialog?.setView(null);
    } catch (e) {
      messagesActions.addMessage({
        type: "error",
        text: "Bei Ihrer Registrierung ist ein Fehler aufgetreten.",
      });
      setIsLoading(false);
    }
  };

  const handleRecover = async (values: FormDataTypes["values"]) => {
    setIsLoading(true);
    try {
      await apiGet("/customers/password_reset", { email: values.email.toLowerCase() });
      messagesActions.addMessage({
        type: "success",
        text: "Wir haben Ihnen eine E-Mail zum Zurücksetzen Ihres Passworts geschickt.",
      });
      onActionTypeChange("login");
    } catch (e) {
      messagesActions.addMessage({
        type: "error",
        text: "Beim Zurücksetzen Ihres Passworts ist ein Fehler aufgetreten.",
      });
    }
    setIsLoading(false);
  };

  const submitAction = async ({ values }: { values: FormDataTypes["values"] }) => {
    switch (actionType) {
      case "login":
        await handleLogin(values);
        return;
      case "register":
        await handleRegister(values);
        return;
      case "recover":
        await handleRecover(values);
        return;
    }
  };

  return (
    <FormProvider initialValues={initialValues} submitAction={submitAction}>
      <AuthActionForm actionType={actionType} setActionType={onActionTypeChange} isLoading={isLoading} showPostfix={showPostfix} />
    </FormProvider>
  );
}
